import {
  Stack,
  Text,
  Icon,
  Badge,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  Box,
  Button,
} from '@customerio/pluma-components/react';
import { useEnvHealth } from '../api/get-env-health';
import { useWorkspaceHealthSimulation } from '../store';
import { SEVERITY_COLOR, collapseProcessingAlerts } from '../utils';
import { useAskAgentForAlert } from '../use-ask-agent-for-alert';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { generatePath } from '@/services/router/generate-path';
import * as styles from './alerts-panel.css';

interface AlertsPanelProps {
  /** When true, return null instead of showing the empty state */
  hideWhenEmpty?: boolean;
  /** Pass true when the user is on the health dashboard (enables heavy polling). */
  active?: boolean;
}

export function AlertsPanel({ hideWhenEmpty = false, active = false }: AlertsPanelProps) {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate, active);
  const data = healthQuery.data;
  const alerts = data != null ? collapseProcessingAlerts(data.alerts) : [];
  const hasAlerts = alerts.length > 0;

  const { enabled: askAgentEnabled, askAgent } = useAskAgentForAlert();

  if (!hasAlerts && hideWhenEmpty) {
    return null;
  }

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <InlineStack gap="050" alignBlock="center">
            Status updates
            {hasAlerts ? <Badge isEmphasized={true}>{data?.alerts.length}</Badge> : null}
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection withPadding={false}>
        {hasAlerts ? (
          <Stack px="300">
            {alerts.slice(0, 5).map((alert, index) => {
              const showAskAgent = askAgentEnabled && alert.copilot_prompt != null;
              const showFailedEventsLink = alert.action_type === 'failed_events';
              return (
                <Stack
                  key={alert.title}
                  gap="050"
                  pt={index == 0 ? undefined : '200'}
                  pb={index == alerts.slice(0, 5).length - 1 ? '300' : '200'}
                  className={styles.categoryGroup}
                >
                  <InlineStack gap="100" alignBlock="center">
                    <StatusIndicator color={SEVERITY_COLOR[alert.severity]} />
                    <Text size="s" weight="semibold">
                      {alert.title}
                    </Text>
                  </InlineStack>
                  <Text size="xs" color="subtle">
                    {alert.message}
                  </Text>
                  {showFailedEventsLink || showAskAgent ? (
                    <InlineStack gap="200" alignBlock="center">
                      {showFailedEventsLink ? (
                        <Box>
                          <Link
                            href={generatePath(
                              '/workspaces/:workspaceId/health',
                              { workspaceId },
                              { drawer: 'data-alerts', tab: 'data' },
                            )}
                            variant="secondary"
                          >
                            {alert.action_label}
                          </Link>
                        </Box>
                      ) : null}
                      {showAskAgent ? (
                        <Button variant="subtle" size="sm" icon="sparkle" onClick={() => askAgent(alert)}>
                          Ask agent
                        </Button>
                      ) : null}
                    </InlineStack>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <InlineStack gap="100" alignBlock="center" px="300" pb="300">
            <Icon name="check-circle" size="sm" color="success" />
            <Text size="s" color="subtle">
              All clear: nothing needs your attention
            </Text>
          </InlineStack>
        )}
      </PanelSection>
    </Panel>
  );
}
